import React, { useMemo, useOptimistic, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useModifyAccount } from '@/hooks/useAccounts';
import { AllIntegrations, useIntegration } from '@/hooks/useIntegration';
import { useMy } from '@/hooks/useMy';
import { EmailNotificationEditForm } from '@/sections/settings/components/forms/EmailNotificationEditForm';
import { ChatMessagingViewModal } from '@/sections/settings/components/modals/ChatMessagingViewModal';
import { EmailViewModal } from '@/sections/settings/components/modals/EmailViewModal';
import { WebhookViewModal } from '@/sections/settings/components/modals/WebhookViewModal';
import { dangerButtonStyle } from '@/sections/settings/constants';
import SetupModal from '@/sections/SetupModal';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { Account, Integration, IntegrationMeta } from '@/types';
import { cn } from '@/utils/classname';
import { getChariotWebhookURL } from '@/utils/integration.util';
import { generateUuid } from '@/utils/uuid.util';

import { ExposureAlertsCard } from './ExposureAlertsCard';
import { getJiraEditFormData } from './getJiraEditFormData';
import {
  CHAT_MESSAGING_INTEGRATIONS,
  ChatMessagingSection,
  EmailNotificationSection,
  InboundWebhookSection,
  ITSM_NOTIFICATION_INTEGRATIONS,
  ITSMSection,
  OutgoingWebhookSection,
} from './notifications';

interface NotificationSettingsCardsProps {
  /**
   * Email for impersonation - if provided, will fetch data for this user
   * If not provided, uses the current authenticated user
   */
  email?: string;

  /**
   * Whether to show edit buttons (default: true)
   */
  editable?: boolean;
}

/**
 * Unified notification settings component grouping all notification-related
 * configuration into a single cohesive interface.
 *
 * Sections (in order):
 * 1. Email Notifications - Email notification management
 * 2. Outgoing Webhook - Webhook notification management
 * 3. Integrations - Slack, Teams, Jira, ServiceNow, Zulip, GChat
 * 4. Exposure Alerts - Port/service exposure monitoring
 * 5. Webhook URL - Incoming webhook endpoint for data push
 *
 * React 19 Features:
 * - useOptimistic: Instant integration disconnect with automatic rollback
 *
 * Used in:
 * - NotificationsTab
 */
export const NotificationSettingsCards: React.FC<
  NotificationSettingsCardsProps
> = ({ email, editable = true }) => {
  const { drawerState } = useGlobalState();

  const [disconnectNotification, setDisconnectNotification] =
    useState<Account>();
  const [editEmailNotification, setEditEmailNotification] = useState<Account>();
  const [viewEmailNotification, setViewEmailNotification] = useState<Account>();
  const [editWebhookNotification, setEditWebhookNotification] =
    useState<Account>();
  const [viewWebhookNotification, setViewWebhookNotification] =
    useState<Account>();
  const [viewChatNotification, setViewChatNotification] = useState<Account>();
  const [editItsmNotification, setEditItsmNotification] = useState<Account>();
  // Direct setup modal state for single-option flows (email, webhook)
  // Bypasses drawer when there's only one option to select
  const [directSetupIntegration, setDirectSetupIntegration] =
    useState<IntegrationMeta>();

  // Inbound webhook copy state for green flash visual feedback
  const [inboundWebhookCopied, setInboundWebhookCopied] = useState(false);

  const {
    data: { connectedNotifications },
  } = useIntegration({ email });

  // Fetch auth context and accounts for inbound webhook
  const { me, friend, api } = useAuth();
  const { data: accounts } = useMy({ resource: 'account' }, { email });
  const hookAccount = accounts?.find(({ member }) => member === 'hook');
  const hookUrl = getChariotWebhookURL({
    api,
    me: friend || me,
    pin: hookAccount?.value,
  });

  // Add optimistic state for instant notification removal
  const [optimisticNotifications, removeNotificationOptimistically] =
    useOptimistic(connectedNotifications, (state, removedKey: string) =>
      state.filter(n => n.key !== removedKey)
    );

  const { mutateAsync: unlink, status: unlinkStatus } =
    useModifyAccount('unlink');
  const { mutateAsync: link } = useModifyAccount('link');

  // Pre-filter notifications into four categories
  const {
    emailNotifications,
    webhookNotifications,
    chatMessagingNotifications,
    itsmNotifications,
  } = useMemo(() => {
    return {
      emailNotifications: optimisticNotifications.filter(
        n => n.member === Integration.email
      ),
      webhookNotifications: optimisticNotifications.filter(
        n => n.member === Integration.webhook
      ),
      chatMessagingNotifications: optimisticNotifications.filter(n =>
        (CHAT_MESSAGING_INTEGRATIONS as readonly Integration[]).includes(
          n.member as Integration
        )
      ),
      itsmNotifications: optimisticNotifications.filter(n =>
        (ITSM_NOTIFICATION_INTEGRATIONS as readonly Integration[]).includes(
          n.member as Integration
        )
      ),
    };
  }, [optimisticNotifications]);

  /**
   * Handles disconnecting a notification integration with optimistic UI update.
   * Card disappears instantly, then API call happens in background.
   * If API call fails, useOptimistic automatically rolls back the change.
   */
  const handleDisconnect = async () => {
    if (!disconnectNotification) return;

    // Optimistic update - card disappears instantly
    removeNotificationOptimistically(disconnectNotification.key);

    // Close modal immediately
    setDisconnectNotification(undefined);

    try {
      // Background mutation
      await unlink({
        username: disconnectNotification.member,
        member: disconnectNotification.member,
        config: disconnectNotification.config,
        value: disconnectNotification.value,
        key: disconnectNotification.key,
        silent: true,
      });

      toast.success('Integration disconnected successfully');
    } catch (error) {
      // useOptimistic automatically rolls back on error
      toast.error('Failed to disconnect integration');
      console.error('Disconnect error:', error);
    }
  };

  /**
   * Opens setup modal directly for single-option flows (email, webhook).
   * Bypasses the drawer since there's only one option to select.
   */
  const handleAddEmail = () => {
    setDirectSetupIntegration(AllIntegrations[Integration.email]);
  };

  const handleAddWebhook = () => {
    setDirectSetupIntegration(AllIntegrations[Integration.webhook]);
  };

  const handleAddItsm = () => {
    drawerState.handleOpenIntegrationDrawer(undefined, 'IT Service Management');
  };

  /**
   * Handles generating a new inbound webhook URL with unique UUID pin
   */
  const handleGenerateInboundWebhook = async () => {
    try {
      await link({
        value: generateUuid(),
        config: {},
        username: 'hook',
        silent: true,
      });
      toast.success('Webhook URL generated successfully');
    } catch (error) {
      console.error('Failed to generate webhook:', error);
      toast.error('Failed to generate webhook URL');
    }
  };

  /**
   * Handles disconnecting the inbound webhook endpoint
   */
  const handleDisconnectInboundWebhook = async () => {
    if (!hookAccount) return;

    try {
      await unlink({
        config: hookAccount.config,
        key: hookAccount.key,
        member: hookAccount.member,
        value: hookAccount.value,
        username: hookAccount.member,
        silent: true,
      });
      toast.success('Webhook disconnected successfully');
    } catch (error) {
      console.error('Failed to disconnect webhook:', error);
      toast.error('Failed to disconnect webhook');
    }
  };

  /**
   * Handles copying inbound webhook URL to clipboard with visual feedback
   */
  const handleCopyInboundWebhook = async () => {
    try {
      await navigator.clipboard.writeText(hookUrl);
      toast.success('Webhook URL copied to clipboard');
      setInboundWebhookCopied(true);
      setTimeout(() => setInboundWebhookCopied(false), 1500);
    } catch (error) {
      console.error('Clipboard write failed:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        {/* SECTION 1: Email Notifications */}
        <EmailNotificationSection
          notifications={emailNotifications}
          onView={setViewEmailNotification}
          onDelete={editable ? setDisconnectNotification : undefined}
          onAdd={editable ? handleAddEmail : undefined}
        />

        {/* SECTION 2: Outgoing Webhook */}
        <OutgoingWebhookSection
          notifications={webhookNotifications}
          onView={setViewWebhookNotification}
          onDelete={editable ? setDisconnectNotification : undefined}
          onAdd={editable ? handleAddWebhook : undefined}
        />

        {/* SECTION 3: Chat Messaging (Slack, Teams, GChat, Zulip) */}
        <ChatMessagingSection
          notifications={chatMessagingNotifications}
          onDelete={editable ? setDisconnectNotification : undefined}
          onView={setViewChatNotification}
          onAdd={
            editable
              ? () =>
                  drawerState.handleOpenIntegrationDrawer(
                    undefined,
                    'Workplace Messaging'
                  )
              : undefined
          }
        />

        {/* SECTION 4: IT Service Management (Jira, ServiceNow) */}
        <ITSMSection
          notifications={itsmNotifications}
          onView={setEditItsmNotification}
          onDelete={editable ? setDisconnectNotification : undefined}
          onAdd={editable ? handleAddItsm : undefined}
          status="success"
        />

        {/* SECTION 5: Exposure Alerts Card */}
        <ExposureAlertsCard customerEmail={email} editable={editable} />

        {/* SECTION 6: Inbound Webhook - for INCOMING data */}
        <InboundWebhookSection
          hookAccount={hookAccount}
          hookUrl={hookUrl}
          onGenerate={editable ? handleGenerateInboundWebhook : undefined}
          onDisconnect={editable ? handleDisconnectInboundWebhook : undefined}
          onCopy={handleCopyInboundWebhook}
          isCopied={inboundWebhookCopied}
        />
      </div>

      {/* Edit Email Notification Modal */}
      <Modal
        title="Edit Email Notification"
        onClose={() => setEditEmailNotification(undefined)}
        open={Boolean(editEmailNotification)}
        hideFooter
        data-testid="edit-email-notification-modal"
      >
        {editEmailNotification && (
          <EmailNotificationEditForm
            integration={editEmailNotification}
            onClose={() => setEditEmailNotification(undefined)}
          />
        )}
      </Modal>

      {/* Edit Webhook Notification Modal */}
      {editWebhookNotification && (
        <SetupModal
          isOpen={Boolean(editWebhookNotification)}
          onClose={() => setEditWebhookNotification(undefined)}
          onComplete={() => setEditWebhookNotification(undefined)}
          selectedIntegration={editWebhookNotification}
          formData={{
            webhook: String(editWebhookNotification.config?.webhook || ''),
            value: editWebhookNotification.value || '',
            header: String(editWebhookNotification.config?.header || ''),
            token: String(editWebhookNotification.config?.token || ''),
            severities: String(
              editWebhookNotification.config?.severities || 'MHC'
            ),
          }}
          integration={AllIntegrations[Integration.webhook]}
          hideAddLater
          parseFormData={data => {
            return { ...data, value: data.webhook };
          }}
        />
      )}

      {/* View Email Modal - view/edit severity only */}
      {viewEmailNotification && (
        <EmailViewModal
          open={Boolean(viewEmailNotification)}
          onClose={() => setViewEmailNotification(undefined)}
          emailAccount={viewEmailNotification}
        />
      )}

      {/* View Webhook Modal - view/edit severity only */}
      {viewWebhookNotification && (
        <WebhookViewModal
          open={Boolean(viewWebhookNotification)}
          onClose={() => setViewWebhookNotification(undefined)}
          webhook={viewWebhookNotification}
        />
      )}

      {/* View Chat Messaging Modal - view/edit severity only */}
      {viewChatNotification && (
        <ChatMessagingViewModal
          open={Boolean(viewChatNotification)}
          onClose={() => setViewChatNotification(undefined)}
          chatAccount={viewChatNotification}
        />
      )}

      {/* Edit Jira Modal - SetupModal for Jira configuration editing */}
      {editItsmNotification &&
        editItsmNotification.member === Integration.jira && (
          <SetupModal
            isOpen={Boolean(editItsmNotification)}
            onClose={() => setEditItsmNotification(undefined)}
            onComplete={() => setEditItsmNotification(undefined)}
            selectedIntegration={editItsmNotification}
            step="configuration"
            integration={AllIntegrations[Integration.jira]}
            formData={getJiraEditFormData(editItsmNotification)}
            key={editItsmNotification.key}
          />
        )}

      {/* Disconnect Modal - follows DeleteApiKeyModal pattern */}
      <Modal
        title={
          disconnectNotification
            ? `Delete ${
                AllIntegrations[disconnectNotification.member as Integration]
                  ?.name || 'Integration'
              } ${disconnectNotification.member === Integration.email ? 'Notification' : 'Integration'}`
            : ''
        }
        open={Boolean(disconnectNotification)}
        onClose={() => setDisconnectNotification(undefined)}
        hideFooter
        data-testid="disconnect-notification-modal"
      >
        <div className="text-chariot-text-secondary text-sm">
          {disconnectNotification?.member === Integration.email
            ? 'Deleting this email notification cannot be undone. Are you sure you want to delete it?'
            : 'Deleting this integration cannot be undone, and will have to be set up again. Are you sure you want to delete it?'}
        </div>

        {/* Custom footer matching EmailNotificationEditForm pattern */}
        <div className="flex items-center justify-end gap-2 pt-4 pb-1">
          <Button
            onClick={() => setDisconnectNotification(undefined)}
            label="Cancel"
            styleType="text"
            className="bg-chariot-background-primary px-4"
            size="md"
            data-testid="disconnect-modal-cancel"
          />
          <Button
            onClick={handleDisconnect}
            label="Delete"
            styleType="primary"
            className={cn('ml-2 min-w-24 px-4', dangerButtonStyle)}
            size="md"
            disabled={unlinkStatus === 'pending'}
            data-testid="disconnect-modal-confirm"
          />
        </div>
      </Modal>

      {/* Direct Setup Modal for Email/Webhook (bypasses drawer) */}
      {directSetupIntegration && (
        <SetupModal
          isOpen={Boolean(directSetupIntegration)}
          onClose={() => setDirectSetupIntegration(undefined)}
          onComplete={() => setDirectSetupIntegration(undefined)}
          selectedIntegration={undefined}
          formData={{}}
          integration={directSetupIntegration}
          hideAddLater
          parseFormData={data => {
            const value =
              data.username === 'email'
                ? data.email
                : data.username === 'jira' || data.username === 'webhook'
                  ? data.value
                  : data.webhook;

            return { ...data, value };
          }}
        />
      )}
    </>
  );
};
